import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IUser } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'user';

export interface IAdminUserPage {
  total: number;
  page: number;
  users: IUser[];
}

/**
 * Admin-side user calls cnt-landing's UserApiService doesn't have (search,
 * delete/restore). Updates and image uploads go through UserApiService
 * directly — those take an explicit user id, and the server lets admins act
 * on any user.
 */
@Injectable({ providedIn: 'root' })
export class AdminUserApiService {
  private readonly http = inject(HttpClient);

  /** `GET /user/paginated` — server matches `searchTerm` against name/email/id; `deleted` lists only deleted users. */
  search(searchTerm: string, page = 1, pageSize = 25, deleted = false): Observable<IAdminUserPage> {
    const query = objectToUrlParams({
      searchTerm: searchTerm.trim() || undefined,
      page,
      pageSize,
      deleted: deleted || undefined,
    });
    return this.http.get<IAdminUserPage>(`${API_URL}/paginated?${query}`);
  }

  fetch(userId: number): Observable<IUser> {
    return this.http.get<IUser>(`${API_URL}/${userId}`);
  }

  /** Soft-deletes the user (`DELETE /user/:id`). */
  remove(userId: number): Observable<IUser> {
    return this.http.delete<IUser>(`${API_URL}/${userId}`);
  }

  restore(userId: number): Observable<IUser> {
    return this.http.post<IUser>(`${API_URL}/${userId}/restore`, null);
  }
}
